import type { RiskClass } from '@ralph/contracts'

/**
 * `approvals` — the card where a person is asked to consent to something.
 *
 * Moved here from `components/chat/ApprovalCard.tsx`, which never had a catalogue of its own
 * (PRD-27 F-14). Two things make that surface a control rather than a confirmation dialog,
 * and both shape the words: the exact payload is shown in full — not summarized — because a
 * person cannot consent to something they were only told about; and the decision carries a
 * digest the server recomputes, so if anything about the proposal changed between display
 * and click the decision is refused rather than applied to a different action.
 *
 * Which is why `payloadTitle` says "Exactly", and why `irreversible` is plain speech rather
 * than the technical word. A consent card is the last place to be clever.
 *
 * English is the source of truth AND the type (LOC-011). Anything that interpolates is a
 * FUNCTION returning a whole sentence (LOC-013).
 */
export const approvals = {
  /**
   * The agent's own name is data (D-17) and arrives as a parameter, so only the sentence
   * around it moves.
   */
  needsApproval: (displayName: string): string => `${displayName} needs your approval`,

  /**
   * A risk class as a word, never as its identifier (D-9, LOC-018). `satisfies` is the whole
   * point: a seventh risk class in the contract is a compile error here rather than a raw
   * `external_communication` in the badge on the one card where a person is being asked to
   * consent to something.
   *
   * The same six values are worded again in `trace.risk`, on purpose (D-9 as amended): a
   * label map lives in the namespace of the surface that renders it, and the consent card
   * and the operator's trace are read in different frames of mind.
   */
  risk: {
    read: 'read',
    reversible_write: 'reversible write',
    external_communication: 'external communication',
    privileged: 'privileged',
    financial: 'financial',
    destructive: 'destructive'
  } satisfies Record<RiskClass, string>,
  /**
   * `PendingApproval.risk` is `string`, not `RiskClass` — the reducer widens it on the way
   * in — so the card has to look the value up rather than index it. This says that this
   * build does not recognise the class instead of printing the class: a reader deciding
   * whether to approve is not helped by a raw token, and dropping the badge entirely would
   * hide the one fact the card exists to state.
   */
  riskUnknown: 'unclassified',

  /** Plain speech on purpose. Not "irreversible", which is a word for a spec, not a decision. */
  irreversible: 'cannot be undone',

  /** The destinations follow in bold, joined by the one `Intl.ListFormat` (LOC-016). */
  dataReaching: 'Data will reach:',

  payloadTitle: 'Exactly what will run',
  editLabel: 'Edit the proposed arguments',
  /**
   * `JSON` is a format abbreviation and never translates (D-17); the parser's own complaint
   * is a browser-supplied string this catalogue has no way to localize and no business
   * rewriting.
   */
  invalidJson: (detail: string): string => `Not valid JSON: ${detail}`,

  notePlaceholder: 'Add a note for the record (optional)',
  noteLabel: 'Decision note',

  approve: 'Approve',
  editFirst: 'Edit first',
  reject: 'Reject',
  approveEdited: 'Approve edited',
  cancelEdit: 'Cancel edit',

  /**
   * `when` is `formatUntil` output — "in 3h", or the word for an instant already past. Both
   * branches have to read as one sentence in BOTH locales (LOC-022), which is why the
   * Portuguese entry uses a colon: "expira em 3 h" is right and "expira expirado" is not.
   */
  expires: (when: string): string => `expires ${when}`
}
